import { cn } from '@heidi/utils';
import { m, useReducedMotion } from 'motion/react';

import { workTurnInFlightGlowClassNames } from './workComposerGlow';

import type { JSX } from 'react';

export interface ComposerAmbientGlowProps {
  visible: boolean;
  testId: string;
}

/** Decorative response-generation light; it never participates in layout or input. */
export const ComposerAmbientGlow = ({
  visible,
  testId,
}: ComposerAmbientGlowProps): JSX.Element => {
  const shouldReduceMotion = useReducedMotion();
  const isLooping = visible && !shouldReduceMotion;

  return (
    <div
      aria-hidden
      data-testid={testId}
      data-visible={String(visible)}
      className={cn(
        workTurnInFlightGlowClassNames.container,
        visible ? 'opacity-100' : 'opacity-0'
      )}
    >
      <m.div
        data-glow-layer="tail"
        className={cn(
          workTurnInFlightGlowClassNames.tail,
          visible && 'will-change-[transform,opacity]'
        )}
        initial={isLooping ? undefined : false}
        animate={
          isLooping
            ? {
                opacity: [0.42, 0.54, 0.48, 0.42],
                x: ['-2%', '3%', '1%', '-2%'],
                y: ['8%', '5%', '7%', '8%'],
                scale: [1.02, 1.06, 1.04, 1.02],
              }
            : {
                opacity: visible ? 0.48 : 0,
                x: '0%',
                y: '0%',
                scale: 1.02,
              }
        }
        transition={
          isLooping
            ? {
                duration: 12,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatType: 'loop',
              }
            : { duration: 0.2, ease: 'easeOut' }
        }
      />
      <m.div
        data-glow-layer="core"
        className={cn(
          workTurnInFlightGlowClassNames.core,
          visible && 'will-change-[transform,opacity]'
        )}
        initial={isLooping ? undefined : false}
        animate={
          isLooping
            ? {
                opacity: [0.48, 0.58, 0.52, 0.46, 0.56, 0.36, 0.48],
                x: ['-1%', '1%', '2%', '1%', '0%', '-2%', '-1%'],
                y: ['16%', '12%', '8%', '14%', '16%', '-8%', '16%'],
                scale: [1, 1.07, 1.12, 1.05, 1, 0.88, 1],
              }
            : {
                opacity: visible ? 0.52 : 0,
                x: '0%',
                y: '14%',
                scale: 1.02,
              }
        }
        transition={
          isLooping
            ? {
                duration: 12,
                ease: 'easeInOut',
                times: [0, 0.18, 0.36, 0.55, 0.72, 0.86, 1],
                repeat: Infinity,
                repeatType: 'loop',
              }
            : { duration: 0.2, ease: 'easeOut' }
        }
      />
    </div>
  );
};
